import * as log from 'loglevel';
import chalk from 'chalk';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __spreadArray(to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
}

var blurple = {
    blurple50: '#f1edfd',
    blurple100: '#e2dafb',
    blurple200: '#c6b5f7',
    blurple300: '#a991f3',
    blurple400: '#8d6cef',
    blurple500: '#7047eb',
    blurple600: '#5a39be',
    blurple700: '#442b91',
    blurple800: '#2f1d63',
    blurple900: '#190f36',
};
var ultramarine = {
    ultramarine50: '#ded3f4',
    ultramarine100: '#cdbdec',
    ultramarine200: '#aa93db',
    ultramarine300: '#8868cb',
    ultramarine400: '#653eba',
    ultramarine500: '#4313aa',
    ultramarine600: '#360f88',
    ultramarine700: '#280b66',
    ultramarine800: '#1b0844',
    ultramarine900: '#0d0422',
};
var burntOrange = {
    burntOrange50: '#fdece7',
    burntOrange100: '#fadad0',
    burntOrange200: '#f5b5a1',
    burntOrange300: '#f18f71',
    burntOrange400: '#ec6a42',
    burntOrange500: '#e74513',
    burntOrange600: '#b9370f',
    burntOrange700: '#8b290b',
    burntOrange800: '#5c1c08',
    burntOrange900: '#2e0e04',
};
var sunGlow = {
    sunGlow50: '#fff9ea',
    sunGlow100: '#fff3d5',
    sunGlow200: '#ffe7ab',
    sunGlow300: '#ffdb80',
    sunGlow400: '#ffcf56',
    sunGlow500: '#ffc32c',
    sunGlow600: '#cd9c23',
    sunGlow700: '#9a761a',
    sunGlow800: '#684f12',
    sunGlow900: '#352909',
};
var sky = {
    sky50: '#f2fbff',
    sky100: '#e4f7ff',
    sky200: '#c9efff',
    sky300: '#afe8ff',
    sky400: '#94e0ff',
    sky500: '#79d8ff',
    sky600: '#61b8dc',
    sky700: '#4998b9',
    sky800: '#307996',
    sky900: '#185973',
};
var midnight = {
    midnight50: '#678edb',
    midnight100: '#5c81c9',
    midnight200: '#4766a3',
    midnight300: '#314b7e',
    midnight400: '#1c3058',
    midnight500: '#061533',
    midnight600: '#051129',
    midnight700: '#040d1f',
    midnight800: '#020814',
    midnight900: '#01040a',
};
var conifer = {
    conifer50: '#f8fff1',
    conifer100: '#f2ffe2',
    conifer200: '#e4ffc6',
    conifer300: '#d7ffa9',
    conifer400: '#c9ff8d',
    conifer500: '#bcff70',
    conifer600: '#9eda5a',
    conifer700: '#80b543',
    conifer800: '#62912d',
    conifer900: '#446c16',
};
var gray = {
    gray50: '#f6f6f6',
    gray100: '#ededed',
    gray200: '#dadada',
    gray300: '#c8c8c8',
    gray400: '#b5b5b5',
    gray500: '#a3a3a3',
    gray600: '#919191',
    gray700: '#7e7e7e',
    gray800: '#6c6c6c',
    gray900: '#595959',
};
var colors$1 = {
    base: {
        black: '#000',
        white: '#fff',
        transparent: 'transparent',
        inherit: 'inherit',
    },
    light: __assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign({}, blurple), ultramarine), burntOrange), sunGlow), sky), midnight), conifer), gray),
    dark: __assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign({}, blurple), ultramarine), burntOrange), sunGlow), sky), midnight), conifer), gray),
};

var colors = colors$1.light;
var commonBrowserStyles = {
    'font-family': 'system-ui',
    'padding-top': '1px',
    'padding-bottom': '2px',
    'font-size': '13',
    'border-radius': '2px',
    'font-weight': '600',
    'letter-spacing': '2px',
    color: 'white',
};
var nodeStyle = {
    prefix: chalk.bold.white.bgHex(colors.blurple500),
    debugTitle: chalk.bold.white.bgHex(colors.ultramarine500),
    errorTitle: chalk.bold.white.bgHex(colors.burntOrange500),
    errorBody: chalk.hex(colors.burntOrange500),
    warnTitle: chalk.bold.hex(colors.midnight500).bgHex(colors.sunGlow500),
    infoTitle: chalk.bold.hex(colors.midnight500).bgHex(colors.sky500),
};
var browserStyle = {
    default: browserStyles(__assign({}, commonBrowserStyles)),
    prefix: browserStyles(__assign(__assign({}, commonBrowserStyles), { background: colors.blurple500 })),
    debugTitle: browserStyles(__assign(__assign({}, commonBrowserStyles), { background: colors.ultramarine500 })),
    errorTitle: browserStyles(__assign(__assign({}, commonBrowserStyles), { background: colors.burntOrange500 })),
    errorBody: browserStyles({
        color: colors.burntOrange500,
    }),
    warnTitle: browserStyles(__assign(__assign({}, commonBrowserStyles), { background: colors.sunGlow500, color: colors.midnight500 })),
    infoTitle: browserStyles(__assign(__assign({}, commonBrowserStyles), { background: colors.sky500, color: colors.midnight500 })),
};
function browserStyles(css) {
    var cssString = '';
    if (typeof css == 'string') {
        cssString = 'color:' + css;
    }
    else {
        for (var key in css) {
            cssString = cssString + key + ':' + css[key] + ';';
        }
    }
    return function () { return cssString; };
}

var IS_BROWSER = typeof window !== 'undefined' && typeof window.document !== 'undefined';
/**
 * The Dopt Logger offers a convenient framework for logLevel configuration
 * and templated logging. Allowing you to enable logging in your packages based
 * on log levels and with defined styled template for your node terminal and
 * browser console. For more, see @dopt/logger's README.md: {@link https://github.com/dopt/odopt/tree/main/packages/@dopt/logger/README.md}.
 */
var Logger = /** @class */ (function () {
    /**
     * The Logger's constructor gets called with the `new` operator,
     * and sets the static instance to newly created instance on construction.
     * If instance is already created it simply returns the reference.
     */
    function Logger(_a) {
        var logLevel = _a.logLevel, prefix = _a.prefix;
        // Default log level is 'silent'
        this.logLevel = 'silent';
        // Default prefix is 'dopt'
        this.prefix = 'Dopt';
        /**
         * The Logger class uses `getLogger` from loglevel to return a unique instance by prefix.
         */
        this.baseLogger = log.getLogger(this.prefix);
        if (!Logger.instance) {
            this.logLevel = logLevel ? logLevel : 'silent';
            this.prefix = prefix ? prefix : this.prefix;
            this.baseLogger = log;
            this.baseLogger.setLevel(this.logLevel);
            Logger.instance = this;
        }
        return Logger.instance;
    }
    /**
     * Finally, any Logger should define some business logic, which can be
     * executed on its instance.
     */
    Logger.prototype.log = function () {
        var _a, _b;
        var msg = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            msg[_i] = arguments[_i];
        }
        IS_BROWSER
            ? (_a = this.baseLogger).log.apply(_a, __spreadArray(["%c".concat(this.prefix), browserStyle.prefix()], msg, false)) : (_b = this.baseLogger).log.apply(_b, __spreadArray([nodeStyle.prefix(this.prefix)], msg, false));
    };
    Logger.prototype.debug = function () {
        var _a, _b;
        var msg = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            msg[_i] = arguments[_i];
        }
        IS_BROWSER
            ? (_a = this.baseLogger).debug.apply(_a, __spreadArray(["%c".concat(this.prefix, "%c %c[Debug]"), browserStyle.prefix(),
                browserStyle.default(),
                browserStyle.debugTitle()], msg, false)) : (_b = this.baseLogger).debug.apply(_b, __spreadArray([nodeStyle.prefix(this.prefix),
            nodeStyle.debugTitle('[Debug]')], msg, false));
    };
    Logger.prototype.error = function () {
        var msg = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            msg[_i] = arguments[_i];
        }
        IS_BROWSER
            ? this.baseLogger.error("%c".concat(this.prefix, "%c %c[Error]%c").concat(msg), browserStyle.prefix(), browserStyle.default(), browserStyle.errorTitle(), browserStyle.errorBody())
            : this.baseLogger.error(nodeStyle.prefix(this.prefix), nodeStyle.errorTitle('[Error]'), nodeStyle.errorBody.apply(nodeStyle, msg));
    };
    Logger.prototype.warn = function () {
        var _a, _b;
        var msg = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            msg[_i] = arguments[_i];
        }
        IS_BROWSER
            ? (_a = this.baseLogger).warn.apply(_a, __spreadArray(["%c".concat(this.prefix, "%c %c[Warn]"), browserStyle.prefix(),
                browserStyle.default(),
                browserStyle.warnTitle()], msg, false)) : (_b = this.baseLogger).warn.apply(_b, __spreadArray([nodeStyle.prefix(this.prefix),
            nodeStyle.warnTitle('[Warn]')], msg, false));
    };
    Logger.prototype.info = function () {
        var _a, _b;
        var msg = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            msg[_i] = arguments[_i];
        }
        IS_BROWSER
            ? (_a = this.baseLogger).info.apply(_a, __spreadArray(["%c".concat(this.prefix, "%c %c[Info]"), browserStyle.prefix(),
                browserStyle.default(),
                browserStyle.infoTitle()], msg, false)) : (_b = this.baseLogger).info.apply(_b, __spreadArray([nodeStyle.prefix(this.prefix),
            nodeStyle.infoTitle('[Info]')], msg, false));
    };
    return Logger;
}());

export { Logger };
